import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, Check, HeartHandshake, Pencil, Plus, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import AvatarCircle from "../components/AvatarCircle";
import QueryState from "../components/QueryState";
import ScreenHeader from "../components/ScreenHeader";
import { errorMessageOf, useAuth } from "../lib/auth";
import { prepareAvatar } from "../lib/avatar";
import { useConfirm } from "../lib/confirm";
import {
  colorOfUser,
  contrastTextColor,
  DEFAULT_PARTNER_COLOR,
  DEFAULT_SELF_COLOR,
  isValidHexColor,
} from "../lib/color";
import { formatDateJa, todayDateKey } from "../lib/dates";
import {
  createAnniversary,
  fetchAnniversaries,
  updateAnniversary,
} from "../lib/schedule/anniversaries";
import {
  createProfileItem,
  deleteProfileItem,
  fetchCoupleProfiles,
  fetchProfileAnswers,
  fetchProfileItems,
  normalizeProfileText,
  saveProfileAnswer,
  updateMyProfile,
  type ProfilePatch,
} from "../lib/profileItems";
import { queryKeys } from "../lib/queryKeys";
import { useToast } from "../lib/toast";
import Reflection from "./Reflection";

/** どの欄を書き換え中か。基本項目は固定キー、任意項目は item: を付けた id */
type EditKey =
  "name" | "birth" | "phone" | "address" | "email" | "dating" | "marriage" | `item:${string}`;

/** ふたりの記念日は、この決まったタイトルの記念日レコードとして保存する
    (予定タブのカウントダウンと共有。"結婚日" は既存レコードとの互換のため変えない) */
const DATING_TITLE = "付き合った日";
const MARRIAGE_TITLE = "結婚日";

/** 消せる(空に戻せる)基本項目と、その profiles 列名 */
const CONTACT_COLUMNS = {
  phone: "phone_number",
  address: "address",
  email: "contact_email",
} as const;

export default function Profile({
  wantsPartner = false,
  onConsumeTarget,
}: {
  /** 通知(プロフィール変更)から開かれた。相手側の表示に切り替える */
  wantsPartner?: boolean;
  onConsumeTarget?: () => void;
}) {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const userId = session?.user.id;
  const avatarInput = useRef<HTMLInputElement | null>(null);

  // 画面上部のタブでどちらのプロフィールを見るか切り替える。
  // 2人並べると1列が狭くなりすぎるため、1人ずつ全幅で見せる
  const [viewingPartner, setViewingPartner] = useState(false);

  useEffect(() => {
    if (!wantsPartner) return;
    setViewingPartner(true);
    onConsumeTarget?.();
  }, [wantsPartner, onConsumeTarget]);
  const [editing, setEditing] = useState<{ key: EditKey; draft: string } | null>(null);
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [showReflection, setShowReflection] = useState(false);

  const profiles = useQuery({ queryKey: queryKeys.coupleProfiles, queryFn: fetchCoupleProfiles });
  const items = useQuery({ queryKey: queryKeys.profileItems, queryFn: fetchProfileItems });
  const answers = useQuery({ queryKey: queryKeys.profileAnswers, queryFn: fetchProfileAnswers });
  const anniversaries = useQuery({
    queryKey: queryKeys.anniversaries,
    queryFn: fetchAnniversaries,
  });

  const dating = anniversaries.data?.find((a) => a.title === DATING_TITLE) ?? null;
  const marriage = anniversaries.data?.find((a) => a.title === MARRIAGE_TITLE) ?? null;

  const self = profiles.data?.find((profile) => profile.id === userId);
  const partner = profiles.data?.find((profile) => profile.id !== userId);
  const shown = viewingPartner ? partner : self;
  const editable = !viewingPartner;
  const shownColor =
    shown === undefined ? DEFAULT_SELF_COLOR : colorOfUser(profiles.data, shown.id, userId);

  const answerOf = (itemId: string, ownerId: string | undefined) =>
    answers.data?.find((answer) => answer.item_id === itemId && answer.user_id === ownerId)?.value;

  /** 書き込みの共通処理。終わったら該当のクエリを取り直す */
  async function run(action: () => Promise<void>, keys: readonly (readonly unknown[])[]) {
    setBusy(true);
    try {
      await action();
      setEditing(null);
      for (const key of keys) {
        await queryClient.invalidateQueries({ queryKey: key });
      }
    } catch (e) {
      toast(errorMessageOf(e), "error");
    } finally {
      setBusy(false);
    }
  }

  function saveMine(patch: ProfilePatch, extraKeys: readonly (readonly unknown[])[] = []) {
    if (userId === undefined) return;
    void run(() => updateMyProfile(userId, patch), [queryKeys.coupleProfiles, ...extraKeys]);
  }

  function submitEditing() {
    if (editing === null || userId === undefined) return;
    const draft = editing.draft;

    if (editing.key === "name") {
      const name = normalizeProfileText(draft);
      if (name === null) return;
      // 表示名はアルバムの「◯◯が追加」やコメント、記念日の名前にも出る
      saveMine({ display_name: name }, [
        queryKeys.birthdays,
        queryKeys.members,
        queryKeys.profile(userId),
      ]);
      return;
    }
    if (editing.key === "birth") {
      if (draft === "") return;
      saveMine({ birth_date: draft }, [queryKeys.birthdays]);
      return;
    }
    if (editing.key === "phone" || editing.key === "address" || editing.key === "email") {
      // 空にしたら null で消す(緊急連絡先は未入力に戻せる)
      saveMine({ [CONTACT_COLUMNS[editing.key]]: normalizeProfileText(draft) });
      return;
    }
    if (editing.key === "dating" || editing.key === "marriage") {
      if (draft === "") return;
      const title = editing.key === "dating" ? DATING_TITLE : MARRIAGE_TITLE;
      const defaultIcon = editing.key === "dating" ? "heart" : "gem";
      const existing = editing.key === "dating" ? dating : marriage;
      void run(async () => {
        if (existing !== null) {
          await updateAnniversary(existing.id, {
            title,
            baseDate: draft,
            kind: "anniversary",
            icon: existing.icon,
            memo: existing.memo,
          });
        } else {
          await createAnniversary({
            title,
            baseDate: draft,
            kind: "anniversary",
            icon: defaultIcon,
            memo: null,
          });
        }
      }, [queryKeys.anniversaries]);
      return;
    }
    const itemId = editing.key.slice("item:".length);
    // 空での保存は「答えを消す」として通す
    void run(() => saveProfileAnswer(itemId, draft), [queryKeys.profileAnswers]);
  }

  function pickAvatar(file: File) {
    if (userId === undefined) return;
    void run(async () => {
      const dataUrl = await prepareAvatar(file);
      await updateMyProfile(userId, { avatar: dataUrl });
    }, [queryKeys.coupleProfiles]);
  }

  function pickFavoriteColor(color: string) {
    if (userId === undefined || !isValidHexColor(color)) return;
    saveMine({ favorite_color: color });
  }

  async function addItem() {
    const label = normalizeProfileText(newLabel);
    if (label === null) return;
    setNewLabel("");
    await run(async () => {
      await createProfileItem(label);
    }, [queryKeys.profileItems]);
  }

  async function removeItem(id: string, label: string) {
    const ok = await confirm({
      message: `項目「${label}」を消します。\nふたりの答えも一緒に消えます。`,
      confirmLabel: "項目を消す",
      tone: "danger",
    });
    if (!ok) return;
    await run(() => deleteProfileItem(id), [queryKeys.profileItems, queryKeys.profileAnswers]);
  }

  /** 自分の欄。押すと書き換えられる。editValue は入力欄に入れる生の値(日付など) */
  function editableCell(
    key: EditKey,
    value: string | undefined,
    options?: { date?: boolean; editValue?: string },
  ) {
    if (editing?.key === key) {
      return (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            submitEditing();
          }}
          className="flex items-center gap-1"
        >
          <input
            autoFocus
            type={options?.date === true ? "date" : "text"}
            max={options?.date === true ? todayDateKey() : undefined}
            maxLength={200}
            value={editing.draft}
            onChange={(event) => {
              setEditing({ key, draft: event.target.value });
            }}
            className="w-full min-w-0 flex-1 rounded-lg border border-[var(--color-line)] bg-white px-2 py-1 text-base"
          />
          <button
            type="submit"
            disabled={busy}
            aria-label="保存"
            className="shrink-0 text-[var(--color-teal)] disabled:opacity-50"
          >
            <Check className="size-5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(null);
            }}
            aria-label="やめる"
            className="shrink-0 text-[var(--color-sub)]"
          >
            <X className="size-5" aria-hidden />
          </button>
        </form>
      );
    }
    return (
      <button
        type="button"
        onClick={() => {
          setEditing({ key, draft: options?.editValue ?? value ?? "" });
        }}
        className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-left"
      >
        <span
          className={`min-w-0 flex-1 break-words ${value === undefined ? "text-[var(--color-sub)]" : ""}`}
        >
          {value ?? "未入力"}
        </span>
        <Pencil className="size-3 shrink-0 text-[var(--color-sub)] opacity-60" aria-hidden />
      </button>
    );
  }

  /** 1行。見ているのが自分なら編集でき、相手なら読むだけ */
  function row(
    label: string,
    key: EditKey,
    value: string | undefined,
    options?: { date?: boolean; editValue?: string },
  ) {
    return (
      <div className="grid grid-cols-[6rem_1fr] items-center gap-2 border-b border-[var(--color-line)] px-3 py-2.5 text-sm">
        <span className="text-xs text-[var(--color-sub)]">{label}</span>
        {editable ? (
          editableCell(key, value, options)
        ) : (
          <span className={`break-words ${value === undefined ? "text-[var(--color-sub)]" : ""}`}>
            {value ?? "未入力"}
          </span>
        )}
      </div>
    );
  }

  const loading =
    profiles.isPending || items.isPending || answers.isPending || anniversaries.isPending;
  const failed = profiles.isError || items.isError || answers.isError || anniversaries.isError;

  return (
    <>
      <ScreenHeader
        navKey="profile"
        menu={[
          {
            label: "ふりかえりメモ",
            icon: HeartHandshake,
            onSelect: () => {
              setShowReflection(true);
            },
          },
        ]}
      />

      <div className="flex flex-col gap-4 p-4">
        <QueryState
          isPending={loading}
          isError={failed}
          errorMessage="プロフィールを取得できませんでした。"
        />

        {!loading && !failed && (
          <>
            {/* どちらを見るか。編集中に切り替えたら編集はやめる */}
            <div className="flex rounded-xl border border-[var(--color-line)] bg-white p-1">
              {[
                { profile: self, isPartner: false, note: "自分" },
                { profile: partner, isPartner: true, note: null },
              ].map(({ profile, isPartner, note }) => {
                const active = viewingPartner === isPartner;
                const tabColor =
                  profile !== undefined
                    ? colorOfUser(profiles.data, profile.id, userId)
                    : isPartner
                      ? DEFAULT_PARTNER_COLOR
                      : DEFAULT_SELF_COLOR;
                return (
                  <button
                    key={isPartner ? "partner" : "self"}
                    type="button"
                    disabled={profile === undefined}
                    onClick={() => {
                      setViewingPartner(isPartner);
                      setEditing(null);
                    }}
                    aria-pressed={active}
                    style={
                      active
                        ? { backgroundColor: tabColor, color: contrastTextColor(tabColor) }
                        : undefined
                    }
                    className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm disabled:opacity-40 ${
                      active ? "" : "text-[var(--color-sub)]"
                    }`}
                  >
                    <AvatarCircle
                      avatar={profile?.avatar}
                      name={profile?.displayName}
                      color={tabColor}
                      className="size-6 text-xs"
                    />
                    <span className="truncate">
                      {profile?.displayName ?? "相手"}
                      {note !== null && <span className="opacity-70">({note})</span>}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="overflow-hidden rounded-xl border border-[var(--color-line)] bg-white">
              {/* アイコン。自分のときはカメラ印から変えられる */}
              <div className="flex flex-col items-center gap-1 border-b border-[var(--color-line)] bg-[var(--color-bg)] py-4">
                {editable ? (
                  <button
                    type="button"
                    onClick={() => avatarInput.current?.click()}
                    disabled={busy}
                    aria-label="アイコンを変更"
                    className="relative disabled:opacity-50"
                  >
                    <AvatarCircle
                      avatar={shown?.avatar}
                      name={shown?.displayName}
                      color={shownColor}
                      className="size-20 text-2xl"
                    />
                    <span className="absolute -right-1 -bottom-1 flex size-6 items-center justify-center rounded-full border border-[var(--color-line)] bg-white">
                      <Camera className="size-3.5 text-[var(--color-sub)]" aria-hidden />
                    </span>
                  </button>
                ) : (
                  <AvatarCircle
                    avatar={shown?.avatar}
                    name={shown?.displayName}
                    color={shownColor}
                    className="size-20 text-2xl"
                  />
                )}
                <span className="text-sm font-medium">{shown?.displayName ?? "―"}</span>
              </div>

              <div className="grid grid-cols-[6rem_1fr] items-center gap-2 border-b border-[var(--color-line)] px-3 py-2.5 text-sm">
                <span className="text-xs text-[var(--color-sub)]">好きな色</span>
                {editable ? (
                  <label className="flex items-center gap-2">
                    <input
                      type="color"
                      value={shown?.favoriteColor ?? DEFAULT_SELF_COLOR}
                      onChange={(event) => {
                        pickFavoriteColor(event.target.value);
                      }}
                      disabled={busy}
                      aria-label="好きな色を選ぶ"
                      className="size-8 cursor-pointer rounded-full border border-[var(--color-line)] bg-white p-0.5 disabled:opacity-50"
                    />
                    <span className="text-xs text-[var(--color-sub)] uppercase">
                      {shown?.favoriteColor ?? DEFAULT_SELF_COLOR}
                    </span>
                  </label>
                ) : (
                  <span className="flex items-center gap-2">
                    <span
                      className="size-5 rounded-full border border-[var(--color-line)]"
                      style={{ backgroundColor: shown?.favoriteColor ?? DEFAULT_PARTNER_COLOR }}
                      aria-hidden
                    />
                    <span className="text-xs text-[var(--color-sub)] uppercase">
                      {shown?.favoriteColor ?? DEFAULT_PARTNER_COLOR}
                    </span>
                  </span>
                )}
              </div>

              {row("表示名", "name", shown?.displayName)}
              {row(
                "生年月日",
                "birth",
                shown?.birthDate !== null && shown?.birthDate !== undefined
                  ? formatDateJa(shown.birthDate)
                  : undefined,
                { date: true, editValue: shown?.birthDate ?? "" },
              )}
              {row("電話番号", "phone", shown?.phone ?? undefined)}
              {row("住所", "address", shown?.address ?? undefined)}
              {row("メール", "email", shown?.contactEmail ?? undefined)}

              {items.data?.map((item) => (
                <div
                  key={item.id}
                  className="grid grid-cols-[6rem_1fr] items-center gap-2 border-b border-[var(--color-line)] px-3 py-2.5 text-sm last:border-b-0"
                >
                  <span className="flex min-w-0 items-center gap-1">
                    <span className="min-w-0 flex-1 text-xs break-words text-[var(--color-sub)]">
                      {item.label}
                    </span>
                    <button
                      type="button"
                      onClick={() => void removeItem(item.id, item.label)}
                      disabled={busy}
                      aria-label={`${item.label} を消す`}
                      className="shrink-0 text-[var(--color-sub)] opacity-60 disabled:opacity-30"
                    >
                      <Trash2 className="size-3.5" aria-hidden />
                    </button>
                  </span>
                  {editable ? (
                    editableCell(`item:${item.id}`, answerOf(item.id, userId))
                  ) : (
                    <span
                      className={`break-words ${
                        answerOf(item.id, shown?.id) === undefined ? "text-[var(--color-sub)]" : ""
                      }`}
                    >
                      {answerOf(item.id, shown?.id) ?? "未入力"}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* ふたりの共通項目。人別ではないので、どちらのタブでも同じものが出る */}
            <div className="overflow-hidden rounded-xl border border-[var(--color-line)] bg-white">
              <div className="border-b border-[var(--color-line)] bg-[var(--color-bg)] px-3 py-2 text-xs text-[var(--color-sub)]">
                ふたりの記念日(どちらでも書き換えられます)
              </div>
              <div className="grid grid-cols-[6rem_1fr] items-center gap-2 border-b border-[var(--color-line)] px-3 py-2.5 text-sm">
                <span className="text-xs text-[var(--color-sub)]">付き合った日</span>
                {editableCell(
                  "dating",
                  dating !== null ? formatDateJa(dating.baseDate) : undefined,
                  { date: true, editValue: dating?.baseDate ?? "" },
                )}
              </div>
              <div className="grid grid-cols-[6rem_1fr] items-center gap-2 px-3 py-2.5 text-sm">
                <span className="text-xs text-[var(--color-sub)]">結婚記念日</span>
                {editableCell(
                  "marriage",
                  marriage !== null ? formatDateJa(marriage.baseDate) : undefined,
                  { date: true, editValue: marriage?.baseDate ?? "" },
                )}
              </div>
            </div>

            <p className="text-xs text-[var(--color-sub)]">
              {editable
                ? "各欄を押すと書き換えられます。連絡先は緊急時に互いに参照できるよう、正しいものを入れておいてください。"
                : "相手のプロフィールは閲覧のみです。"}
            </p>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                void addItem();
              }}
              className="flex gap-2"
            >
              <input
                value={newLabel}
                onChange={(event) => {
                  setNewLabel(event.target.value);
                }}
                maxLength={50}
                placeholder="新しい項目(例: 好きな食べ物)"
                className="flex-1 rounded-lg border border-[var(--color-line)] bg-white px-3 py-2 text-base"
              />
              <button
                type="submit"
                disabled={busy || normalizeProfileText(newLabel) === null}
                className="flex items-center gap-1 rounded-lg bg-[var(--color-teal)] px-4 py-2 text-sm text-white disabled:opacity-50"
              >
                <Plus className="size-4" aria-hidden />
                追加
              </button>
            </form>
          </>
        )}
      </div>

      <input
        ref={avatarInput}
        type="file"
        accept="image/*,.heic,.heif"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file !== undefined) pickAvatar(file);
          event.target.value = "";
        }}
      />

      {showReflection && (
        <Reflection
          onClose={() => {
            setShowReflection(false);
          }}
        />
      )}
    </>
  );
}
